import { Component, inject, signal } from '@angular/core';
import { IdName, ToteRentalType } from '../../../../../interfaces/pricematrixmodel';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LookUpService } from '../../../../../services/lookupservice';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { formatDateOnly } from '../../../../shared/commonutils/helper';
import { AlertDialogComponent } from '../../../../shared/components/alert-dialog/alert-dialog.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
@Component({
  selector: 'app-addedit-rentaltype',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatDialogModule,
    DragDropModule
  ],
  templateUrl: './addedit-rentaltype.component.html',
  styleUrl: './addedit-rentaltype.component.scss'
})
export class AddeditRentaltypeComponent {
  private dialogRef = inject(MatDialogRef<AddeditRentaltypeComponent>);
  private data: ToteRentalType = inject(MAT_DIALOG_DATA);
  private lookup: LookUpService = inject(LookUpService);
  private fb = inject(FormBuilder);

  // Component State
  form!: FormGroup;
  rentalTypes = signal<IdName[]>([]);
  sizes = signal<IdName[]>([]);
  displayValue = signal<string>('');
  
  isEditMode = signal(false);
  dialogTitle = signal('');

  constructor(private dialog: MatDialog) {
    console.log("AddEditRentalType Dialog data:", this.data);
  }

  ngOnInit(): void {
    this.loaddropdowns();
    const defaultData: Partial<ToteRentalType> = {
      task: 'Add',
      id: 0,
      rentalTypeId: null,
      fee: null,
      quantity: null,
      rentalSize: null,
      startDate: null,
      endDate: null,
    };

    // Merge injected data with defaults
    const initialData = { ...defaultData, ...this.data };

    this.isEditMode.set(initialData.task === 'Edit');
    this.dialogTitle.set(this.isEditMode() ? `Edit Rental Type (ID: ${initialData.id})` : 'Add Rental Type');

    // Initialize Reactive Form with all fields mandatory
    this.form = this.fb.group({
      rentalTypeId: [initialData.rentalTypeId, Validators.required],
      fee: [initialData.fee, [Validators.required, Validators.min(0)]],
      quantity: [initialData.quantity, [Validators.required, Validators.min(1)]],
      rentalSize: [initialData.rentalSize, Validators.required],
      startDate: [initialData.startDate ? new Date(initialData.startDate) : null, Validators.required],
      endDate: [initialData.endDate ? new Date(initialData.endDate) : null, Validators.required],
      
      // Hidden/Contextual fields
      id: [initialData.id],
      priceMatrixId: [initialData.priceMatrixId, Validators.required],
    });

    // Handle initial selection for Rental Type Name/Size based on ID if needed
    if (initialData.rentalTypeId) {
      const selectedType = this.rentalTypes().find(t => t.id === initialData.rentalTypeId);
      if (selectedType) {
          // Optional: You might need to update a rentalTypeName field if it exists
      }
    }
  }

  loaddropdowns(): void {
    this.lookup.getToteRentalTypes().subscribe(types => {
      this.rentalTypes.set(types);
    });

    this.lookup.getToteRentalSizes().subscribe(sizes => {
      this.sizes.set(sizes);
      if (!this.isEditMode() && this.form.get('rentalSize')?.value === null && sizes.length > 0) {
        this.form.get('rentalSize')?.setValue(sizes[0].name);
      }
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.error("Form is invalid. Cannot save.");
      return;
    }
    
    const formValue: ToteRentalType = this.form.value;

    let startDate = formValue.startDate ? new Date(formValue.startDate) : null;
    let endDate = formValue.endDate ? new Date(formValue.endDate) : null;
    if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'End date should be after Start date.'
        },
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const selectedType = this.rentalTypes().find(t => t.id === formValue.rentalTypeId);
    
    const result: ToteRentalType = {
        ...formValue,
        task: this.isEditMode() ? 'Edit' : 'Add',
        startDate: formValue.startDate ? formatDateOnly(formValue.startDate) : null,
        endDate: formValue.endDate ? formatDateOnly(formValue.endDate) : null,
        rentalTypeName: selectedType ? selectedType.name : 'Unknown',
        size: this.sizes().find(s => s.name === formValue.rentalSize)?.id || null
    };

    console.log("Saving Rental Type:", result);

    this.dialogRef.close(result);
  }
  onKeydown(event: KeyboardEvent) {
    // Allow: Backspace, Tab, End, Home, Left, Right, Delete, and date separators
    const allowedKeys = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete', '/', '-'];
    const isNumber = /[0-9]/.test(event.key);
    if (!isNumber && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    if (maxDecimals === 0) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      val = val.replace(/[^0-9.]/g, '');
    }
    
    const points = val.split('.');
    if (points.length > 2) {
      val = points[0] + '.' + points.slice(1).join('');
    }
    
    const maxIntegerLength = maxValue.toString().length;
    const parts = val.split('.');
    let intPart = parts[0];
    let decPart = parts.length > 1 ? parts[1] : null;

    if (intPart.length > maxIntegerLength) {
      intPart = intPart.substring(0, maxIntegerLength);
      val = decPart !== null ? `${intPart}.${decPart}` : intPart;
    }
    
    if (decPart !== null && decPart.length > maxDecimals) {
      decPart = decPart.substring(0, maxDecimals);
      val = `${intPart}.${decPart}`;
    }
    
    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.form.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.form.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.form.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
  });
  }
}
